// @flow
import React from "react";
import { useTranslation } from "react-i18next";
import { List } from "semantic-ui-react";
import { DateTime } from "luxon";

import { ExpandableSegment } from "Commons/Components";
import { formatConsumption, formatCurrency, formatTimePeriod } from "modules/Offers/misc/Utils";
import TariffRow from "./TariffRow";

import type { OfferT } from "modules/Offers/misc/Types";
import type { ReminderT } from "modules/Reminder/misc/Types";

import Styles from "./TariffComparison.module.scss";

type Props = {
  reminder: ReminderT,
  offer: OfferT,
};

const NewTariff = ({ reminder, offer }: Props) => {
  const { t } = useTranslation("offerPageDetail");

  if (!offer) return null;
  if (!offer.gasElectricityDetails) return null;

  const details = offer.gasElectricityDetails;
  const contract = offer.contractDetails;
  const costWithoutBonus = details.usageCosts + details.fixedCosts;
  const costPerUnit = parseFloat(details.pricePerUnit).toFixed(2).toString();
  const monthly = costWithoutBonus / 12.0;
  const bonus = details.sumOfOneTimeBonuses || 0;
  const remindMeBonus = offer.remindMeBonus;

  return (
    <ExpandableSegment
      attached
      toggleOnHeaderClick
      className={Styles.tariff}
      header={
        <div className={Styles.tariffHeader}>
          <List>
            <TariffRow
              labelBold
              label={t("tariffsComparison.newTariff")}
              value={`${formatCurrency(
                remindMeBonus?.amount
                  ? remindMeBonus.totalRunningCostAmount
                  : offer.gasElectricityDetails?.totalCostAmount || 0
              )} / Jahr`}
            />
          </List>
        </div>
      }
    >
      <List>
        <TariffRow label={t("specialOffer:offer.provider")} value={offer.companyDetails.name} />
        <TariffRow label={t("specialOffer:offer.demand")} value={formatConsumption(reminder)} />
        <TariffRow label={t("specialOffer:offer.tariff")} value={offer.productName} />
        <TariffRow label={t("specialOffer:offer.fixedCosts")} value={formatCurrency(details.fixedCosts)} />
        <TariffRow
          label={t("specialOffer:offer.ecoTariff")}
          value={details.ecoTariff ? t("specialOffer:offer.yes") : t("specialOffer:offer.no")}
        />
        <TariffRow
          label={t("specialOffer:offer.costPerUnit")}
          value={`${costPerUnit} ${t("specialOffer:offer.costPerUnitUnit")}`}
        />
        <TariffRow label={t("specialOffer:offer.costWithoutBonus")} value={formatCurrency(costWithoutBonus)} />
        <TariffRow
          label={t("specialOffer:offer.monthlyPayment")}
          value={`${formatCurrency(monthly)} ${t("specialOffer:offer.monthlyPaymentUnit")}`}
        />
        {!!bonus && <TariffRow label={t("specialOffer:offer.providerBonus")} value={formatCurrency(bonus)} />}
        {!!remindMeBonus?.amount && (
          <TariffRow
            primaryColor
            label={t("specialOffer:offer.remindmeBonus")}
            value={formatCurrency(remindMeBonus.amount)}
          />
        )}
        <TariffRow
          label={t("specialOffer:offer.priceGuaranteeDuration")}
          value={
            details.guaranteedUntil
              ? "bis " + DateTime.fromISO(details.guaranteedUntil).toFormat("dd.MM.yyyy")
              : details.priceGuaranteeDuration + " Monate"
          }
        />
        <TariffRow
          label={t("specialOffer:offer.contractLength")}
          value={formatTimePeriod(contract.contractDurationAmount || contract.period, contract.contractDurationUnit, t)}
        />
        <TariffRow
          label={t("specialOffer:offer.firstYearPrice")}
          value={formatCurrency(
            remindMeBonus?.amount ? remindMeBonus?.totalRunningCostAmount : details.totalCostAmount
          )}
        />
      </List>
    </ExpandableSegment>
  );
};

export default NewTariff;
